<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      キャンセルポリシー・注意点
    </Header>

    <CancelPolicyContent>
      <template v-if="applicationId">
        <OrderFixedButton hasSub @click="onClickLastConfirm">
          最終確認へ
          <template #sub>
            ボタンを押すと上記内容を同意することになります
          </template>
        </OrderFixedButton>
      </template>
    </CancelPolicyContent>
    <template v-if="willBeOutbreakingInfection && beforeWorkerReliability">
      <ApplicationsCancelDueToInfectionModal
        :currentWorkerReliability="beforeWorkerReliability"
        :noCloseOnBackdrop="true"
        :noCloseOnEsc="true"
        @click-confirm="onClickConfirmButton"
        @click-close="onClickCloseButton"
      />
    </template>
    <template
      v-if="
        !willBeOutbreakingInfection &&
          afterWorkerReliability &&
          beforeWorkerReliability
      "
    >
      <ApplicationsCancelWarningModal
        :will-ban="willBan"
        :beforeWorkerReliability="beforeWorkerReliability"
        :afterWorkerReliability="afterWorkerReliability"
        :application-id="applicationId"
        :noCloseOnBackdrop="true"
        :noCloseOnEsc="true"
        @click-confirm="onClickConfirmButton"
        @click-close="onClickCloseButton"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { applicationService } from "@/app/services/ApplicationService";
import { Component, Prop, Vue } from "vue-facing-decorator";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import ApplicationsCancelWarningModal from "@/components/organisms/applications/ApplicationsCancelWarningModal.vue";
import ApplicationsCancelDueToInfectionModal from "@/components/organisms/applications/ApplicationsCancelDueToInfectionModal.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import OrderFixedButton from "@/components/organisms/orders/OrderFixedButton.vue";
import CancelPolicyContent from "@/components/organisms/order/CancelPolicyContent.vue";
import { WorkerReliability } from "@/types/WorkerReliability";
import { Application } from "@/types/order/Application";
import { isBefore, parse } from "@/utils/date";

@Component({
  components: {
    ApplicationsCancelWarningModal,
    ApplicationsCancelDueToInfectionModal,
    FeatherIcon,
    Header,
    CancelPolicyContent,
    OrderFixedButton
  }
})
export default class OrdersCancelPolicy extends Vue {
  beforeWorkerReliability: WorkerReliability | null = null;
  afterWorkerReliability: WorkerReliability | null = null;
  application: Application | null = null;

  @Prop({
    type: Number,
    required: false
  })
  applicationId!: number | null;

  get willBan() {
    return this.afterWorkerReliability?.creditScore === 0;
  }

  async fetchTryCancel() {
    if (!this.applicationId) return;
    const { before, after } = await applicationService.tryCancelAvailableCount(
      this.applicationId
    );
    this.beforeWorkerReliability = before;
    this.afterWorkerReliability = after;
  }

  // 案件の募集日に感染症が発生しているかをチェック
  get willBeOutbreakingInfection() {
    if (
      !this.application ||
      !this.application.order.office.expectedConvergenceDate
    ) {
      return false;
    }
    const expectedConvergenceDate = parse(
      this.application.order.office.expectedConvergenceDate,
      "yyyy-MM-dd"
    );
    const orderDate = parse(this.application.order.date, "yyyy-MM-dd");
    return !isBefore(expectedConvergenceDate, orderDate);
  }

  async fetchApplication() {
    if (!this.applicationId) return;
    this.application = await applicationService.fetchApplication(
      this.applicationId
    );
  }

  async onClickBack() {
    if (this.applicationId) {
      this.$router.back();
    } else {
      await this.$router.push({ name: "Mypage" });
    }
  }

  async onClickLastConfirm() {
    if (this.applicationId) {
      await this.$router.push({
        name: "ApplicationCancelReason",
        params: {
          applicationId: this.applicationId
        },
        query: {
          cancelType: this.willBeOutbreakingInfection ? "infection" : undefined
        }
      });
    }
  }

  onClickCloseButton() {
    this.$router.back();
  }

  onClickConfirmButton() {
    if (this.willBeOutbreakingInfection) {
      this.$bvModal.hide("confirm-cancel-due-to-infection");
    } else {
      this.$bvModal.hide("confirm-cancel");
    }
  }

  async created() {
    try {
      this.$loading.show();
      await this.fetchTryCancel();
      await this.fetchApplication();
      if (this.willBeOutbreakingInfection) {
        this.$bvModal.show("confirm-cancel-due-to-infection");
      } else {
        this.$bvModal.show("confirm-cancel");
      }
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }
}
</script>
